import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import NavButtons from '../components/NavButtons';
import StepHeading from '../components/StepHeading';
import { BD, BD_TICK_ACTIVE, BD_TICK_IDLE } from '../bookDemoTheme';
import { LOCATION_STOP_COUNT, MAX_EXACT_COUNT, TWENTY_PLUS_INDEX } from '../bookDemoConstants';

interface LocationsStepProps {
  /** 1–20 for an exact count, null when "20+" is selected. */
  count: number | null;
  twentyPlus: boolean;
  onChange: (count: number | null, twentyPlus: boolean) => void;
  onBack: () => void;
  onContinue: () => void;
}

const LAST_INDEX = LOCATION_STOP_COUNT - 1;
/** Minor ruler ticks drawn between two consecutive stops. */
const TICKS_BETWEEN = 3;

const stopPercent = (index: number) => (index / LAST_INDEX) * 100;

/**
 * On mobile only the 1st, every 5th and the final "20+" stop carry a label — and the
 * plain "20" is dropped there because it sits one stop away from "20+" and would collide.
 */
const isMobileLabelled = (index: number) =>
  index === 0 || index === LAST_INDEX || ((index + 1) % 5 === 0 && index !== LAST_INDEX - 1);

const StopLabel: React.FC<{ label: string; active: boolean; labelledOnMobile: boolean }> = ({
  label,
  active,
  labelledOnMobile,
}) => (
  <span className="relative flex items-center justify-center w-[30px] h-[30px] sm:w-[34px] sm:h-[42px]">
    <span
      className={`absolute left-1/2 top-[2px] sm:top-[4px] z-10 h-[2px] w-[2px] sm:h-[3px] sm:w-[3px] -translate-x-1/2 rounded-full ${
        active ? BD.accentBg : BD.stopDot
      }`}
    />
    <span
      className={`relative z-[1] flex h-full w-full items-end justify-center pb-[2px] text-center text-[10px] leading-4 sm:text-[13px] sm:leading-5 font-semibold whitespace-nowrap ${
        active ? BD.accentText : BD.textPrimary
      } ${labelledOnMobile ? '' : 'hidden sm:flex'}`}
    >
      {label}
    </span>
  </span>
);

const LocationsStep: React.FC<LocationsStepProps> = ({ count, twentyPlus, onChange, onBack, onContinue }) => {
  const { t } = useTranslation();

  const selectedIndex = twentyPlus ? TWENTY_PLUS_INDEX : Math.min(Math.max((count ?? 1) - 1, 0), LAST_INDEX);
  const activePct = stopPercent(selectedIndex);

  const selectIndex = (index: number) =>
    index === TWENTY_PLUS_INDEX ? onChange(null, true) : onChange(index + 1, false);

  const stopLabel = (index: number) =>
    index === TWENTY_PLUS_INDEX ? t('bookDemo.locations.twentyPlus') : String(index + 1);

  const valueLabel = twentyPlus
    ? t('bookDemo.locations.valueTwentyPlus')
    : t('bookDemo.locations.value', { count: count ?? 1 });

  /**
   * The value card doubles as a number field: the ruler only reaches 20, so anyone typing more
   * lands on the "20+" stop — which is exactly what the server stores (null + the 20+ flag).
   */
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const applyDraft = (digits: string) => {
    const parsed = parseInt(digits, 10);
    if (!Number.isFinite(parsed) || parsed < 1) return;
    if (parsed > MAX_EXACT_COUNT) onChange(null, true);
    else onChange(parsed, false);
  };

  const handleDraftChange = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, 3);
    setDraft(digits);
    applyDraft(digits);
  };

  const startEditing = () => {
    setDraft(twentyPlus ? '' : String(count ?? 1));
    setEditing(true);
  };

  // An empty or sub-1 entry is not a choice — the current selection simply stays.
  const stopEditing = () => {
    applyDraft(draft);
    setEditing(false);
  };

  const ticks: { pct: number; major: boolean }[] = [];
  for (let stop = 0; stop < LAST_INDEX; stop++) {
    const start = stopPercent(stop);
    const end = stopPercent(stop + 1);
    ticks.push({ pct: start, major: true });
    for (let minor = 1; minor <= TICKS_BETWEEN; minor++) {
      ticks.push({ pct: start + ((end - start) * minor) / (TICKS_BETWEEN + 1), major: false });
    }
  }
  ticks.push({ pct: stopPercent(LAST_INDEX), major: true });

  return (
    <div className="flex flex-col flex-1">
      <StepHeading title={t('bookDemo.locations.title')} subtitle={t('bookDemo.locations.subtitle')} />

      <div className="max-w-[762px] mx-auto w-full px-[8px] sm:px-0">
        <div className={`rounded-xl text-center mb-7 ${BD.rulerFrame}`}>
          <div className={`px-6 py-6 sm:py-[38px] rounded-lg ${BD.cardBg}`}>
            {editing ? (
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoFocus
                value={draft}
                onChange={e => handleDraftChange(e.target.value)}
                onBlur={stopEditing}
                onKeyDown={e => {
                  if (e.key === 'Enter') stopEditing();
                  if (e.key === 'Escape') setEditing(false);
                }}
                aria-label={t('bookDemo.locations.title')}
                className={`mx-auto block w-[120px] text-center text-[28px] sm:text-[32px] leading-8 font-semibold focus:outline-none ${BD.cardBg} ${BD.textPrimary}`}
              />
            ) : (
              <button
                type="button"
                onClick={startEditing}
                className={`mx-auto flex items-center justify-center gap-2 text-[28px] sm:text-[32px] leading-8 font-semibold ${BD.textPrimary}`}
              >
                {valueLabel}
                <Pencil className={`h-4 w-4 flex-shrink-0 ${BD.textMuted}`} />
              </button>
            )}
            <p className={`mt-2 text-[12px] leading-4 ${BD.textMuted}`}>{t('bookDemo.locations.editHint')}</p>
          </div>
        </div>

        <div className="relative w-full select-none px-5 sm:px-[34px] h-[100px] sm:h-[120px]">
          <div className="relative w-full h-full">
            <div className="absolute left-0 right-0 top-[28px] sm:top-[36px] h-10">
              {ticks.map((tick, i) => {
                const nearActive = Math.abs(tick.pct - activePct) < 1.2;
                return (
                  <span
                    key={i}
                    className="absolute bottom-0"
                    style={{
                      left: `${tick.pct}%`,
                      transform: 'translateX(-50%)',
                      width: 1,
                      height: tick.major ? 40 : 16,
                      backgroundColor: nearActive ? BD_TICK_ACTIVE : BD_TICK_IDLE,
                    }}
                  />
                );
              })}
            </div>

            <div
              className="absolute top-0 z-10 flex flex-col items-center pointer-events-none h-[68px] sm:h-[78px]"
              style={{ left: `${activePct}%`, transform: 'translateX(-50%)' }}
            >
              <span className={`h-1.5 w-1.5 sm:h-2.5 sm:w-2.5 flex-shrink-0 rounded-full ${BD.accentBg}`} />
              <span className={`w-0.5 flex-1 ${BD.accentBg}`} />
            </div>

            {Array.from({ length: LOCATION_STOP_COUNT }, (_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => selectIndex(index)}
                aria-label={stopLabel(index)}
                className="absolute bottom-0 z-[5] outline-none"
                style={{ left: `${stopPercent(index)}%`, transform: 'translateX(-50%)' }}
              >
                <StopLabel
                  label={stopLabel(index)}
                  active={index === selectedIndex}
                  labelledOnMobile={isMobileLabelled(index)}
                />
              </button>
            ))}

            <input
              type="range"
              min={0}
              max={LAST_INDEX}
              step={1}
              value={selectedIndex}
              onChange={e => selectIndex(Number(e.target.value))}
              className="absolute inset-x-0 top-6 z-20 h-12 cursor-pointer opacity-0"
              aria-label={t('bookDemo.locations.title')}
            />
          </div>
        </div>
      </div>

      <NavButtons onBack={onBack} onContinue={onContinue} />
    </div>
  );
};

export default LocationsStep;
